import { useEffect, useState } from 'react'
import Pagination from '../components/commons/Pagination'
import { getAllConditions, updateCondition } from '../firebase/conditions'
import { formatDate } from '../utils'
import { useAtom } from 'jotai'
import { showCreateConditionModalAtom, showManageConditionModalAtom } from '../store'
import DialogCreateCondition from '../components/dialogs/DialogCreateCondition'
import DialogEditCondition from '../components/dialogs/DialogEditCondition'

const ConditionItem = ({ condition, handleToggleState, handleEditCondition, loadingStates }) => {
    const isLoading = loadingStates[condition.id] || false

    return (
        <tr key={condition.id} className='h-[70px] hover:bg-[#2F2F2F] p-2'>
            <td>
                {condition.label}
            </td>

            <td>
                {condition.value}
            </td>

            <td>
                {condition.created_at && condition.created_at.seconds ? formatDate(condition.created_at.seconds) : 'N/A'}
            </td>

            <td>
                <div className='flex justify-end pr-10 gap-2'>
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded-[33px]"
                        onClick={() => handleEditCondition(condition)}
                    >
                        Edit
                    </button>
                    <button
                        className={`px-6 py-2 rounded-[33px] flex items-center ${
                            condition.state === 'active' 
                                ? 'bg-green-500 text-white' 
                                : 'bg-yellow-500 text-white'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !isLoading && handleToggleState(condition)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-4 h-4 animate-spin mr-2" />
                                Updating...
                            </>
                        ) : (
                            condition.state === 'active' ? 'Active' : 'Pending'
                        )}
                    </button>
                </div>
            </td>
        </tr>
    )
}

const ConditionManagement = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [conditions, setConditions] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updatedTimes, setUpdatedTimes] = useState(0)
    const [showCreateConditionModal, setShowCreateConditionModal] = useAtom(showCreateConditionModalAtom)
    const [showManageConditionModal, setShowManageConditionModal] = useAtom(showManageConditionModalAtom)
    const [selectedCondition, setSelectedCondition] = useState(null)
    const [loadingStates, setLoadingStates] = useState({})
    const fields = ["Label", 'Value', 'Date added', 'Actions']

    useEffect(() => {
        updateConditions()
    }, [showCreateConditionModal, showManageConditionModal])

    const updateConditions = () => {
        getAllConditions(["active", "pending"]).then((conds) => {
            setConditions(conds)
            setLoading(false)
        })

        setUpdatedTimes(updatedTimes + 1)
    }

    const handleToggleState = async (condition) => {
        // Set loading state for this specific condition
        setLoadingStates(prev => ({ ...prev, [condition.id]: true }))
        
        try {
            const newState = condition.state === 'active' ? 'pending' : 'active'
            await updateCondition({
                id: condition.id,
                label: condition.label,
                value: condition.value,
                state: newState
            })
            updateConditions() // Refresh the data
        } catch (error) {
            console.error('Error updating condition state:', error)
        } finally {
            // Clear loading state for this specific condition
            setLoadingStates(prev => ({ ...prev, [condition.id]: false }))
        }
    }

    const handleEditCondition = (condition) => {
        setSelectedCondition(condition)
        setShowManageConditionModal(true)
    }

    return (
        <>
            <DialogCreateCondition
                open={showCreateConditionModal}
                onClose={() => setShowCreateConditionModal(false)}
            />

            {selectedCondition && (
                <DialogEditCondition
                    open={showManageConditionModal}
                    condition={selectedCondition}
                    onClose={() => setShowManageConditionModal(false)}
                />
            )}

            <div className="px-10 py-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-primary text-[36px]">Condition Manager</h1>

                    <button className="px-6 py-2 bg-white rounded-[33px]" onClick={() => setShowCreateConditionModal(true)}>Add New</button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading conditions...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="my-10">
                            <div className="w-full overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#2E2E2E] rounded-[4px] py-3 text-sm text-[#717171]">
                                            {
                                                fields.map((item, index) =>
                                                    <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`condition-field-${item}-${index}`}>
                                                        {item}
                                                    </th>
                                                )
                                            }
                                        </tr>
                                    </thead>

                                    <tbody className="text-white space-y-1 text-sm">
                                        {
                                            conditions.slice(8 * (currentPage - 1), 8 * currentPage).map(condition =>
                                                <ConditionItem key={condition.id} condition={condition} handleToggleState={handleToggleState} handleEditCondition={handleEditCondition} loadingStates={loadingStates} />
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <Pagination itemsPerPage={8} totalItems={conditions.length} onPageChange={(v) => { setCurrentPage(v) }} />
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default ConditionManagement 